import { type ReactElement, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { autoSignInUrl, signInUrl } from "@/lib/api";

/**
 * Not signed in: try the sign-in without a click (D47), else offer the button.
 *
 * The app cookie lasts 30 idle minutes, the identity service's session usually longer, so
 * the redirect mostly comes straight back signed in. `replace`, not `assign`: "Zurück"
 * must not land on this screen and start the next round. The markers in the address
 * (`autoSignInUrl`) stop it after "Abmelden", after a failed sign-in and on the way back
 * from one, which is what keeps an account without a permission from looping.
 *
 * Also the screen for a signed-in account without a role in this cell: the API answers
 * both with one status, on purpose (telling them apart would let anybody probe which
 * cells an account is known in).
 */
export function SignInScreen({
  title,
  path,
}: {
  readonly title: string;
  readonly path: string;
}): ReactElement {
  const [target] = useState(() => autoSignInUrl(path, window.location));
  const started = useRef(false);
  const signedOut = new URLSearchParams(window.location.search).has("abgemeldet");

  useEffect(() => {
    // StrictMode runs this twice in development; one sign-in transaction is enough.
    if (target === null || started.current) {
      return;
    }
    started.current = true;
    window.location.replace(target);
  }, [target]);

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-4 px-4 text-center">
      <h1 className="text-xl font-bold">{title}</h1>
      {target === null ? (
        <>
          {signedOut ? (
            <p className="text-sm text-muted-foreground">Sie sind abgemeldet.</p>
          ) : null}
          <Button asChild>
            <a href={signInUrl(path, window.location)}>Anmelden</a>
          </Button>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">Anmeldung läuft …</p>
      )}
    </div>
  );
}

/** Once signed in, drop the sign-in markers from the address, so a reload is a plain page again. */
export function useForgetSignInMarkers(ready: boolean): void {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const query = new URLSearchParams(search);
    if (!ready || (!query.has("anmeldung") && !query.has("abgemeldet"))) {
      return;
    }
    query.delete("anmeldung");
    query.delete("abgemeldet");
    const rest = query.toString();
    void navigate({ pathname, search: rest ? `?${rest}` : "", hash }, { replace: true });
  }, [ready, pathname, search, hash, navigate]);
}
